import { cancel, render } from "timeago.js";
import { registerFeatureBootstrap } from "./bootstrap";

export type TimeagoTarget = ParentNode | HTMLTimeElement | HTMLTimeElement[];

export type WMUTimeagoAPI = {
  init: (target?: TimeagoTarget) => void;
  destroy: (target?: TimeagoTarget) => void;
};

type TimeagoWindow = Window & {
  WMUTimeago?: WMUTimeagoAPI;
  __WMU_TIMEAGO_BOOTED?: boolean;
};

const SELECTOR = "time.timeago[datetime]";
const LOCALE = "en_US";

function toTimeElements(target: TimeagoTarget = document): HTMLTimeElement[] {
  if (target instanceof HTMLTimeElement) {
    return target.matches(SELECTOR) ? [target] : [];
  }
  if (Array.isArray(target)) {
    return target.filter((node): node is HTMLTimeElement => node instanceof HTMLTimeElement && node.matches(SELECTOR));
  }
  const matches = Array.from(target.querySelectorAll<HTMLTimeElement>(SELECTOR));
  if (target instanceof HTMLElement && target.matches(SELECTOR)) {
    matches.unshift(target as HTMLTimeElement);
  }
  return matches;
}

function init(target: TimeagoTarget = document): void {
  const nodes = toTimeElements(target);
  if (nodes.length === 0) {
    return;
  }
  nodes.forEach((node) => cancel(node));
  render(nodes, LOCALE);
  nodes.forEach((node) => {
    node.dataset.timeagoReady = "true";
  });
}

function destroy(target: TimeagoTarget = document): void {
  const nodes = toTimeElements(target);
  if (nodes.length === 0) {
    return;
  }
  nodes.forEach((node) => cancel(node));
  nodes.forEach((node) => {
    delete node.dataset.timeagoReady;
  });
}

const timeagoEventCleanup = new WeakMap<HTMLElement, () => void>();

function mountTimeagoEvents(root: HTMLElement): void {
  const doc = root.ownerDocument;
  const onRefresh = (event: Event): void => {
    if (event instanceof CustomEvent && event.detail instanceof HTMLElement) {
      init(event.detail);
      return;
    }
    init(doc);
  };
  doc.addEventListener("wmu:timeago-refresh", onRefresh);
  timeagoEventCleanup.set(root, () => {
    doc.removeEventListener("wmu:timeago-refresh", onRefresh);
  });
}

function unmountTimeagoEvents(root: HTMLElement): void {
  timeagoEventCleanup.get(root)?.();
  timeagoEventCleanup.delete(root);
}

const timeagoWindow = window as TimeagoWindow;

if (!timeagoWindow.__WMU_TIMEAGO_BOOTED) {
  timeagoWindow.__WMU_TIMEAGO_BOOTED = true;
  timeagoWindow.WMUTimeago = { init, destroy };
  registerFeatureBootstrap({
    key: "timeago:times",
    selector: SELECTOR,
    mount(root) {
      init(root);
    },
    unmount(root) {
      destroy(root);
    },
  });
  registerFeatureBootstrap({
    key: "timeago:events",
    selector: "body",
    mount: mountTimeagoEvents,
    unmount: unmountTimeagoEvents,
  });
}
